import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Spinner,
  Text,
  VStack,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { FiUsers, FiCreditCard, FiTrendingUp } from 'react-icons/fi';
import { BiMoneyWithdraw } from 'react-icons/bi';

const API_BASE = '/api';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  useEffect(() => {
    fetch(`${API_BASE}/dashboard/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setStats(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const formatCurrency = (amount, currency = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="teal.500" />
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6}>Dashboard</Heading>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="teal.100" borderRadius="lg">
              <Icon as={FiUsers} boxSize={6} color="teal.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Total Customers</StatLabel>
              <StatNumber fontSize="2xl">{stats?.total_customers || 0}</StatNumber>
            </VStack>
          </HStack>
        </Stat>

        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="green.100" borderRadius="lg">
              <Icon as={FiCreditCard} boxSize={6} color="green.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Active Subscriptions</StatLabel>
              <StatNumber fontSize="2xl">{stats?.active_subscriptions || 0}</StatNumber>
              <StatHelpText mb={0}>{stats?.total_subscriptions || 0} total</StatHelpText>
            </VStack>
          </HStack>
        </Stat>

        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="blue.100" borderRadius="lg">
              <Icon as={BiMoneyWithdraw} boxSize={6} color="blue.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Total Revenue</StatLabel>
              <StatNumber fontSize="2xl">{formatCurrency(stats?.total_revenue || 0)}</StatNumber>
              <StatHelpText mb={0}>{stats?.successful_payments || 0} payments</StatHelpText>
            </VStack>
          </HStack>
        </Stat>

        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="orange.100" borderRadius="lg">
              <Icon as={FiTrendingUp} boxSize={6} color="orange.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Monthly Revenue</StatLabel>
              <StatNumber fontSize="2xl">{formatCurrency(stats?.monthly_revenue || 0)}</StatNumber>
              <StatHelpText mb={0}>Last 30 days</StatHelpText>
            </VStack>
          </HStack>
        </Stat>
      </SimpleGrid>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Heading size="md" mb={4}>Recent Payments</Heading>
        {stats?.recent_payments?.length > 0 ? (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Customer</Th>
                <Th>Amount</Th>
                <Th>Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {stats.recent_payments.map(payment => (
                <Tr key={payment.id}>
                  <Td>{payment.customer_email}</Td>
                  <Td>
                    <Badge colorScheme="green">
                      {formatCurrency(payment.amount, payment.currency)}
                    </Badge>
                  </Td>
                  <Td>{new Date(payment.created_at).toLocaleDateString()}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <Text color="gray.500">No recent payments</Text>
        )}
      </Box>
    </Box>
  );
}

export default Dashboard;
